import { useTheme } from '@mui/styles';
import {
    AdminRoleId,
    AgGridColumnDef,
    AgGridFilterType,
    GridSettingsContext as IGridSettingsContext,
    SecondaryApiTypeEnum
} from '@tassat/common-definitions';
import { FirmForm } from '@tassat/forms';
import React from 'react';

import {
    getCollateral, getTransactions, submitFirmForm, updateFirm
} from 'Containers/admin/forms/helpers/firmFormFn';
import { getAssetBalancesTileData } from 'Lib/accountBalancesHelper';
import { hasApiCapabilities } from 'Lib/apiConnectionsHelper';
import history from 'Lib/browserHistory';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import { useAppSelector } from 'Reducers/store/hooks';
import { getAccountPositionsStats } from 'Selectors/accountPositions';
import { getApiConnectionsArray } from 'Selectors/apiConnections';
import { getAllAssetsArray, getAllSecurityCurrencyPairs } from 'Selectors/assets';
import { getCurrentRole } from 'Selectors/common';
import { getAllFirmApiTypesArray } from 'Selectors/firmApiTypes';
import { getAPIFirmsArray, getFirmsArray } from 'Selectors/firms';
import { getNetAssetValuesAsCurCode } from 'Selectors/netAssetValues';
import AssetList from 'SharedComponents/assetList';
import { useClientThemeData } from 'SharedComponents/hooks/useClientThemeData';
import { useFeatures } from 'SharedComponents/hooks/useFeatures';
import SideListLayout, { SidelistLayoutFormOptions } from 'SharedComponents/layouts/sideListLayout';
import { GridSettingsContext } from 'SharedComponents/layouts/sideListLayout/gridSettingsContext';
import ActionStatusModal from 'SharedComponents/modals/actionStatusModal';
import PendingClientValidationStatusModal from 'SharedComponents/modals/pendingClientValidationStatusModal';
import { SideListGridOptions } from 'SharedComponents/sideList';

import FirmsDashboard from './firmsDashboard';
import {
    getColumnDefs, getExportFunction, getFilterOptions, getSortOptions
} from './firmsGridOptions';

import { GridFirmData } from './';

export const PAGE_TITLE = 'Firms';
const cellHeight = 90;

const setInitialState = () => {
    return ({
        isOpen: false,
        data: {
            firmName: '',
            FirstName: '',
            LastName: ''
        }
    });
};

export const Firms: React.FC = () => {
    const userRole = useAppSelector(getCurrentRole);
    const apiConnections = useAppSelector((state) => (userRole.AdminRoleId !== AdminRoleId.API_OPS ?
        getApiConnectionsArray(state)
        : null));
    const navPriceList = useAppSelector(getNetAssetValuesAsCurCode);
    const isApiAdminRole = userRole.AdminRoleId === AdminRoleId.API_OPS;
    const assetPairs = useAppSelector(getAllSecurityCurrencyPairs);
    const availableAssets = useAppSelector(getAllAssetsArray);
    const firmsList = useAppSelector((state) => (isApiAdminRole ? getAPIFirmsArray(state, '', false) : getFirmsArray(state, '', true)));

    // TODO Modified vv selector to add user role condition - Check firms.tsx Redux HOC
    const isCollateral = firmsList?.filter((firm) => firm.IsCollateral);
    const accountBalancesStats = useAppSelector((state) => getAccountPositionsStats(
        state, true, ((isCollateral && isCollateral.length > 0) ? true : false)
    ));
    const allFirmApiTypes = useAppSelector((state) => getAllFirmApiTypesArray(state, '', false));

    const [ isDeleteModalOpen, setIsDeleteModalOpen ] = React.useState<boolean>(false);
    const [ isPendingUserReqStatusModalConfig, setIsPendingUserReqStatusModalConfig ] = React.useState<{
        isOpen: boolean;
        data?: any;
    }>(setInitialState());
    const [ assetListOverlayOpen, setAssetListOverlayOpen ] = React.useState<boolean>(false);

    // TODO Update to handle multiple API Secondary Connections
    const secondaryApiConnectionExists = apiConnections?.some((api) => api.APISecondaryConnectionType === SecondaryApiTypeEnum.FIREBLOCKS);

    const assetListBalances = React.useMemo(() => {
        if (accountBalancesStats && assetPairs.length > 0) {
            return getAssetBalancesTileData(assetPairs, accountBalancesStats);
        }

        return [];
    }, [ accountBalancesStats, assetPairs ]);

    const navPriceMap = React.useMemo(() => {
        if (!navPriceList) return {};

        return assetPairs.reduce((acc, pair) => {
            if (!pair.security?.AssetId || !pair.security?.Ticker) return acc;
            const entry = Object.values(navPriceList)
                .find(v => v?.AssetId === pair.security?.AssetId);
            if (entry) acc[pair.security.Ticker] = entry;

            return acc;
        }, {} as Record<string, any>);
    }, [ navPriceList, assetPairs ]);

    const theme = useTheme<ApplicationMuiTheme>();
    const [features] = useFeatures();
    const clientInfo = useClientThemeData();

    const gridFirmList = React.useMemo((): Array<GridFirmData> => {
        if (features.fireblocks.api && firmsList) {
            return firmsList.map((firm) => {
                return {
                    ...firm,
                    HasApiConnections: hasApiCapabilities({
                        tokenApi: firm.IsTokenAPIFirm,
                        secondaryApiList: allFirmApiTypes?.filter((api) => api.FirmId === firm.FirmId)
                    })
                };
            });
        }

        return firmsList || [];
    }, [ features.fireblocks.api, firmsList, allFirmApiTypes ]);

    const layoutSettingsMemo: IGridSettingsContext = React.useMemo(() => ({
        pageTitle: PAGE_TITLE,
        title: 'All Firms',
        // The below are no longer taken from the Context in Side List
        columnDefs: [],
        options: { cellHeight: 0 }
    }
    ), [userRole]);

    const columnDefs: AgGridColumnDef[] = React.useMemo(() => getColumnDefs(
        cellHeight,
        theme
    ), [ userRole, theme ]);

    const gridOptions: SideListGridOptions = React.useMemo(() => ({
        idField: 'FirmId',
        cellHeight: cellHeight,
        filterOptions: getFilterOptions(userRole, theme),
        defaultFilter: {
            title: 'Closed Firms',
            colId: 'IsClosed',
            type: AgGridFilterType.Equal,
            filter: 'false'
        },
        sortOptions: getSortOptions(userRole, theme),
        exportData: getExportFunction(userRole, clientInfo.names.applicationName, features),
        searchByColumn: 'Name'
    }), [ clientInfo?.names.applicationName, theme, userRole, features ]);

    const firmFormOptions: SidelistLayoutFormOptions = React.useMemo(() => ({
        formTitle: userRole.IsManageFirmAllowed ? 'Add New Firm' : '',
        Form: userRole.IsManageFirmAllowed ? FirmForm : undefined,
        formModalWidth: '744px',
        formProps: {
            submitFirmForm: (values, opts) => submitFirmForm(values, opts),
            getTransactions: getTransactions,
            getCollateral: getCollateral,
            updateFirm: updateFirm,
            firms: firmsList,
            features,
            secondaryApiConnectionExists,
            availableAssets
        }
    }), [ userRole, features, firmsList, availableAssets ]);

    const onCloseDeleteUserPendingModal = () => {
        setIsDeleteModalOpen(!isDeleteModalOpen);
    };

    const handleIsPendingUserReqStatusModal = () => {
        setIsPendingUserReqStatusModalConfig((prevState) => ({
            ...prevState,
            isOpen: !prevState.isOpen
        }));
    };

    return (
        <GridSettingsContext.Provider value={layoutSettingsMemo}>
            {assetListOverlayOpen ?
                <AssetList
                    assetListBalances={assetListBalances}
                    goBack={() => history.push('/admin/firms')}
                    navPrice={navPriceMap}
                /> : (
                    <>
                        <SideListLayout
                            columnDefs={columnDefs}
                            formOptions={firmFormOptions}
                            gridOptions={gridOptions}
                            isDeleteModalOpen={isDeleteModalOpen}
                            onCloseDeleteUserPendingModal={onCloseDeleteUserPendingModal}
                            rowData={gridFirmList}
                        >
                            <FirmsDashboard setAssetListOverlayOpen={setAssetListOverlayOpen} />
                        </SideListLayout>
                        <ActionStatusModal
                            BodyComponent={
                                <PendingClientValidationStatusModal
                                    data={isPendingUserReqStatusModalConfig.data}
                                    hadleOnClick={() => setIsPendingUserReqStatusModalConfig(setInitialState())}
                                />
                            }
                            onClose={handleIsPendingUserReqStatusModal}
                            open={isPendingUserReqStatusModalConfig.isOpen}
                            validationFailure={false}
                        />
                    </>
                )}
        </GridSettingsContext.Provider>
    );
};